/**
 * Choix des SKILLCARDS sur lesquelles porte une évaluation (plusieurs).
 * Candidates : les SKILLCARDS de l'élève (« Mes SKILLCARDS ») puis celles des
 * packs de son année, regroupées par pack — une puce par groupe, et « Tout le
 * pack » pour cocher d'un coup tout un pack (« le contrôle porte sur
 * Pythagore »). Les cartes choisies s'affichent en puces retirables.
 */

import { useId, useState } from 'react';
import { Check, X } from 'lucide-react';
import { Button, MathText } from '@jeraisonne/ui';
import { stripMath } from '@jeraisonne/db/cards';
import { matchesQuery } from '@/lib/contact-search';
import {
  PickerField,
  PickerModal,
  PickerOption,
  type PickerChip,
} from '@/components/pickers/picker-modal';
import { EVALUATION_MAX_CARDS, type EvaluationCardRef } from '@/lib/evaluations/grades';

export const MY_CARDS_GROUP = 'Mes SKILLCARDS';

export interface CardCandidate {
  id: string;
  title: string;
  group: string;
}

/**
 * Candidates sans doublon : ses SKILLCARDS d'abord, puis les cartes des packs
 * de l'année (une carte présente dans plusieurs packs garde le premier).
 */
export function buildCardCandidates(
  myCards: readonly { id: string; title: string }[],
  packs: readonly { title: string; cards: readonly { id: string; title: string }[] }[],
): CardCandidate[] {
  const seen = new Set<string>();
  const out: CardCandidate[] = [];
  for (const c of myCards) {
    if (seen.has(c.id)) continue;
    seen.add(c.id);
    out.push({ id: c.id, title: c.title, group: MY_CARDS_GROUP });
  }
  for (const p of packs) {
    for (const c of p.cards) {
      if (seen.has(c.id)) continue;
      seen.add(c.id);
      out.push({ id: c.id, title: c.title, group: p.title });
    }
  }
  return out;
}

export function filterCardCandidates(
  items: readonly CardCandidate[],
  query: string,
  group: string,
): CardCandidate[] {
  return items.filter(
    (c) => (!group || c.group === group) && matchesQuery(query, stripMath(c.title), c.group),
  );
}

/** Ajoute / retire une carte (plafond `EVALUATION_MAX_CARDS`). */
export function toggleCardRef(
  value: readonly EvaluationCardRef[],
  card: { id: string; title: string },
): EvaluationCardRef[] {
  if (value.some((v) => v.card_id === card.id)) {
    return value.filter((v) => v.card_id !== card.id);
  }
  if (value.length >= EVALUATION_MAX_CARDS) return [...value];
  return [...value, { card_id: card.id, card_title: card.title }];
}

export function EvaluationCardsPicker({
  candidates,
  value,
  onChange,
}: {
  candidates: readonly CardCandidate[];
  value: readonly EvaluationCardRef[];
  onChange: (next: EvaluationCardRef[]) => void;
}) {
  const fieldId = useId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [group, setGroup] = useState('');
  const groups: PickerChip[] = [...new Set(candidates.map((c) => c.group))].map((g) => ({
    value: g,
    label: g,
  }));
  const shown = filterCardCandidates(candidates, query, group);
  const selectedIds = new Set(value.map((v) => v.card_id));
  const packCards = group ? candidates.filter((c) => c.group === group) : [];
  const allPackSelected = packCards.length > 0 && packCards.every((c) => selectedIds.has(c.id));

  function selectWholePack() {
    let next = [...value];
    for (const c of packCards) {
      if (!next.some((v) => v.card_id === c.id)) next = toggleCardRef(next, c);
    }
    onChange(next);
  }

  return (
    <div className="space-y-2">
      <PickerField
        id={fieldId}
        label="Sur quelles SKILLCARDS ?"
        placeholder={
          candidates.length === 0
            ? 'Aucune SKILLCARD disponible pour ton niveau'
            : 'Choisir les SKILLCARDS (facultatif)'
        }
        disabled={candidates.length === 0}
        onOpen={() => setOpen(true)}
        selected={
          value.length > 0 ? (
            <span className="text-ink-900 font-semibold">
              {value.length} SKILLCARD{value.length > 1 ? 'S' : ''} choisie
              {value.length > 1 ? 's' : ''}
            </span>
          ) : null
        }
      />
      {value.length > 0 && (
        <ul className="flex flex-wrap gap-1.5" aria-label="SKILLCARDS choisies">
          {value.map((v) => (
            <li
              key={v.card_id}
              className="bg-primary-50 ocean:bg-primary-500/15 text-primary-800 ocean:text-primary-100 flex max-w-full items-center gap-1 rounded-full py-0.5 pl-3 pr-0.5 text-sm"
            >
              <span className="truncate">
                <MathText>{v.card_title}</MathText>
              </span>
              <button
                type="button"
                aria-label={`Retirer ${stripMath(v.card_title)}`}
                onClick={() => onChange(value.filter((x) => x.card_id !== v.card_id))}
                className="-my-2 flex h-11 w-9 shrink-0 items-center justify-center"
              >
                <X className="h-4 w-4" aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      )}
      {open && (
        <PickerModal
          title="SKILLCARDS de l’éval"
          query={query}
          onQuery={setQuery}
          searchPlaceholder="Rechercher une SKILLCARD…"
          chips={groups}
          chipValue={group}
          onChip={setGroup}
          allChipsLabel="Toutes"
          onClose={() => setOpen(false)}
          footer={
            <div className="flex flex-col gap-2">
              {group && packCards.length > 1 && !allPackSelected && (
                <Button variant="outline" fullWidth onClick={selectWholePack}>
                  Tout le pack ({packCards.length})
                </Button>
              )}
              <Button fullWidth onClick={() => setOpen(false)}>
                Valider ({value.length})
              </Button>
            </div>
          }
        >
          <div role="listbox" aria-multiselectable="true" className="space-y-1">
            {shown.map((c) => {
              const on = selectedIds.has(c.id);
              return (
                <PickerOption
                  key={c.id}
                  label={stripMath(c.title)}
                  selected={on}
                  onSelect={() => onChange(toggleCardRef(value, c))}
                >
                  <span className="min-w-0 flex-1">
                    <span className="text-ink-900 line-clamp-2 block text-sm font-semibold">
                      <MathText>{c.title}</MathText>
                    </span>
                    <span className="text-ink-500 block truncate text-xs">{c.group}</span>
                  </span>
                  {on && <Check className="text-primary-600 h-5 w-5 shrink-0" aria-hidden />}
                </PickerOption>
              );
            })}
            {shown.length === 0 && (
              <p className="text-ink-500 px-2 py-6 text-center text-sm">
                Aucune SKILLCARD trouvée.
              </p>
            )}
          </div>
        </PickerModal>
      )}
    </div>
  );
}
